/**
 * TricksPanel — Freeze (FREEZE_WHEEL) + Push (PUSH_WHEEL) в фазе TRICKS.
 * Сквад: обе кнопки заблокированы + бейдж (как на макете).
 * Соло: один трик на раунд — после использования тот же паттерн + «You used your trick».
 */

import { useCallback } from "react"
import { motion } from "framer-motion"

import {
  usePvpRoomMyBetType,
  useLivePvpRoomPhase,
  useLivePvpHubUi,
  usePvpRoomRoundInfo,
} from "../hooks/usePvpRoomSelectors"

import { usePvpRoomsStore } from "@/entities/pvp-roulette"

import { cn } from "@/shared/lib/utils"
import { applyTrick } from "@/shared/api"
import { getUserIdFromToken } from "@/shared/api/auth"
import {
  TRICK_TYPE_FREEZE_WHEEL,
  TRICK_TYPE_PUSH_WHEEL,
} from "@/shared/api/types/pvpRoulette"
import type { PvpRouletteTrickType } from "@/shared/api/endpoints"
import { useI18n } from "@/shared/i18n"
import { AnalyticsEvent, trackAnalyticsEvent } from "@/shared/lib/analytics"
import { useDemoModeStore } from "@/shared/store"

import FreezeButton from "@/assets/textures/freeze-button.png"
import PushButton from "@/assets/textures/push-button.png"

export const TricksPanel = ({
  roomId,
  currentUserId,
}: {
  roomId: string
  currentUserId?: string | null
}) => {
  const { t } = useI18n()

  const phase = useLivePvpRoomPhase(roomId)
  const hubUi = useLivePvpHubUi(roomId, { hubScope: "tricksOnly" })
  const roundInfo = usePvpRoomRoundInfo(roomId)
  const rawRound = usePvpRoomsStore((s) => s.rooms[roomId]?.rawRound)
  const optimisticTrickPending = usePvpRoomsStore(
    (s) => s.rooms[roomId]?.optimisticTrickPending
  )
  const applyTrickOptimistic = usePvpRoomsStore((s) => s.applyTrickOptimistic)
  const allowedTricks = rawRound?.allowedTricks ?? []
  const tricks = rawRound?.tricks ?? []
  const userId = currentUserId ?? getUserIdFromToken()
  const myBetType = usePvpRoomMyBetType(roomId, userId)
  const hasMyBet = myBetType != null
  const squadBlocksTricks = myBetType === "team"

  const userUsedAnyTrick =
    userId != null &&
    (tricks.some((trick) => trick.userId === userId) ||
      optimisticTrickPending?.userId === userId)

  const isTricksPhase = phase === "TRICKS"
  const byType = hubUi.canApplyTrickByType

  const tricksWindowOpen =
    hubUi.remainingMsTricks != null && hubUi.remainingMsTricks > 0

  const showPush = allowedTricks.includes(TRICK_TYPE_PUSH_WHEEL)
  const showFreeze = allowedTricks.includes(TRICK_TYPE_FREEZE_WHEEL)

  const canPush =
    isTricksPhase &&
    tricksWindowOpen &&
    showPush &&
    !squadBlocksTricks &&
    (byType[TRICK_TYPE_PUSH_WHEEL] ?? false)
  const canFreeze =
    isTricksPhase &&
    tricksWindowOpen &&
    showFreeze &&
    !squadBlocksTricks &&
    (byType[TRICK_TYPE_FREEZE_WHEEL] ?? false)

  const dualRow = showFreeze && showPush

  const tricksVisuallyBlocked = squadBlocksTricks || userUsedAnyTrick

  const applyTrickOfType = useCallback(
    (trickType: PvpRouletteTrickType) => {
      if (!roundInfo?.roundId) return
      // Apply locally first so the wheel reacts immediately; HTTP only confirms.
      // When Centrifugo echoes the same trick, roundDiff has no TrickAdded vs this snapshot.
      const didOptimistic = applyTrickOptimistic(
        roomId,
        trickType,
        userId ?? undefined
      )
      if (!didOptimistic) return

      trackAnalyticsEvent(AnalyticsEvent.TrickApplied, {
        trick_type: trickType,
        round_id: roundInfo.roundId,
      })

      if (useDemoModeStore.getState().active) return

      void applyTrick({
        round_id: roundInfo.roundId,
        trick_type: trickType,
      }).catch((err) => {
        console.error("[TricksPanel] applyTrick failed:", err)
      })
    },
    [applyTrickOptimistic, roomId, roundInfo?.roundId, userId]
  )

  const handlePush = useCallback(() => {
    if (!canPush) return
    void applyTrickOfType(TRICK_TYPE_PUSH_WHEEL)
  }, [applyTrickOfType, canPush])

  const handleFreeze = useCallback(() => {
    if (!canFreeze) return
    void applyTrickOfType(TRICK_TYPE_FREEZE_WHEEL)
  }, [applyTrickOfType, canFreeze])

  if (!isTricksPhase) return null
  if (!hasMyBet) return null
  if (!showFreeze && !showPush) return null

  const centerBadge = tricksVisuallyBlocked ? (
    <div
      className="pointer-events-none absolute inset-0 z-[2]"
      aria-live="polite"
    >
      <div
        role="status"
        className={cn(
          "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
          "rounded-full bg-white px-2 py-1 text-center",
          "font-bold leading-[19px] text-black",
          "text-base tracking-[-0.5px]",
          "whitespace-nowrap"
        )}
      >
        {squadBlocksTricks
          ? t("tricks.cantUseTricksInSquadMode")
          : t("tricks.usedYourTrick")}
      </div>
    </div>
  ) : null

  return (
    <div
      className={cn(
        "relative isolate mx-auto w-full max-w-2xl",
        "flex flex-row items-center gap-2",
        "px-4 pb-2 pt-3"
      )}
    >
      <div className="relative flex min-h-[60px] w-full flex-1 flex-row items-center gap-2">
        {showFreeze && (
          <motion.button
            onClick={handleFreeze}
            disabled={!canFreeze}
            whileHover={canFreeze ? { scale: 1.02 } : {}}
            whileTap={canFreeze ? { scale: 0.98 } : {}}
            className={cn(
              "relative z-0 flex h-[60px] flex-1 flex-row items-center justify-center",
              "touch-manipulation",
              (tricksVisuallyBlocked || !canFreeze) && "opacity-40"
            )}
          >
            <img className="select-none" src={FreezeButton} />
          </motion.button>
        )}

        {showPush && (
          <motion.button
            type="button"
            onClick={handlePush}
            disabled={!canPush}
            whileHover={canPush ? { scale: 1.02 } : {}}
            whileTap={canPush ? { scale: 0.98 } : {}}
            className={cn(
              "relative z-[1] flex h-[60px] flex-1 flex-row items-center justify-center",
              "touch-manipulation",
              (tricksVisuallyBlocked || !canPush) && "opacity-40"
            )}
          >
            <img className="select-none" src={PushButton} />
          </motion.button>
        )}

        {centerBadge}
      </div>
    </div>
  )
}
